import React, { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { format, parseISO } from "date-fns";
import "../../../styles/charts/DailyLeadPerformanceChart.scss";
import CommonSelect from "../../../../../../core/common/commonSelect";

interface DailyLeadPerformanceProps {
  data: any;
}

const DailyLeadPerformanceChart: React.FC<DailyLeadPerformanceProps> = ({
  data,
}) => {
  const [selectedPipeline, setSelectedPipeline] = useState<string>("-1");

  const chartData = useMemo(() => {
    if (selectedPipeline === "-1") {
      return data.total;
    }
    return data.pipeline_wise[selectedPipeline] || [];
  }, [data, selectedPipeline]);

  const { chartWidth, hasActivity } = useMemo(() => {
    const dataLength = chartData.length;
    const minBarWidth = 40;
    const calculatedWidth = Math.max(dataLength * minBarWidth, 800);

    const hasActivity = chartData.some(
      (item: any) =>
        item.new > 0 ||
        item.converted > 0 ||
        item.lost > 0 ||
        item.in_process > 0
    );

    return {
      chartWidth: calculatedWidth,
      hasActivity,
    };
  }, [chartData]);

  const pipelineOptions =
    data?.pipelines?.map((pipeline: any) => ({
      value: pipeline?.id?.toString(),
      label: pipeline?.pipeline_name,
    })) || [];

  const formatXAxis = (dateString: string) => {
    const date = parseISO(dateString);
    const dataLength = chartData.length;

    if (dataLength <= 14) {
      return format(date, "MMM dd,EEE");
    } else if (dataLength <= 31) {
      return format(date, "MMM dd");
    } else {
      return format(date, "dd");
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const date = parseISO(label);
      const data = payload[0]?.payload;

      return (
        <div className="performance-tooltip">
          <p className="tooltip-date">{format(date, "EEEE, MMM dd, yyyy")}</p>
          <div className="tooltip-content">
            <div className="tooltip-item">
              <div className="tooltip-label">
                <div className="tooltip-dot new-leads" />
                <span>Total New</span>
              </div>
              <span className="tooltip-value">{data?.new || 0}</span>
            </div>
            <div className="tooltip-item">
              <div className="tooltip-label">
                <div className="tooltip-dot converted" />
                <span>Converted</span>
              </div>
              <span className="tooltip-value">{data?.converted || 0}</span>
            </div>
            <div className="tooltip-item">
              <div className="tooltip-label">
                <div className="tooltip-dot in-process" />
                <span>In Process</span>
              </div>
              <span className="tooltip-value">{data?.in_process || 0}</span>
            </div>
            <div className="tooltip-item">
              <div className="tooltip-label">
                <div className="tooltip-dot lost" />
                <span>Lost</span>
              </div>
              <span className="tooltip-value">{data?.lost || 0}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="daily-performance-chart-container">
      <div className="d-flex gap-3 align-items-center justify-content-center">
        <div style={{ width: "200px" }}>
          <CommonSelect
            className="select small text-muted"
            options={[
              { value: "-1", label: "All Pipelines" },
              ...pipelineOptions,
            ]}
            defaultValue={
              selectedPipeline
                ? pipelineOptions.find(
                    (pipeline: any) => pipeline.value === selectedPipeline
                  )
                : undefined
            }
            onChange={(selected: any) => {
              setSelectedPipeline(selected.value);
            }}
            placeHolder={"Filter By Pipeline"}
          />
        </div>

        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-dot new-leads" />
            <span className="legend-text">Total New</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot converted" />
            <span className="legend-text">Converted</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot in-process" />
            <span className="legend-text">In Process</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot lost" />
            <span className="legend-text">Lost</span>
          </div>
        </div>
      </div>

      <div className="chart-wrapper">
        {hasActivity ? (
          <div className="scrollable-chart">
            <LineChart
              width={chartWidth}
              height={400}
              data={chartData}
              margin={{ top: 20, right: 30, left: 0, bottom: 50 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                horizontal={true}
                vertical={true}
              />

              <XAxis
                dataKey="date"
                tickFormatter={formatXAxis}
                interval={chartData?.length < 14 ? 0 : 1}
                angle={0}
                textAnchor="middle"
                height={60}
                tick={{ fontSize: 11, fill: "#6b7280" }}
                stroke="#d1d5db"
                tickLine={true}
              />

              <YAxis
                tick={{ fontSize: 11, fill: "#6b7280" }}
                stroke="#d1d5db"
                width={60}
                tickLine={true}
              />

              <Tooltip content={<CustomTooltip />} />

              <Line
                type="monotone"
                dataKey="new"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{
                  r: 4,
                  fill: "#3b82f6",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                activeDot={{
                  r: 6,
                  fill: "#3b82f6",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                name="New Leads"
              />

              <Line
                type="monotone"
                dataKey="converted"
                stroke="#10b981"
                strokeWidth={2}
                dot={{
                  r: 3,
                  fill: "#10b981",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                activeDot={{
                  r: 5,
                  fill: "#10b981",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                name="Converted"
              />

              <Line
                type="monotone"
                dataKey="in_process"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{
                  r: 3,
                  fill: "#f59e0b",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                activeDot={{
                  r: 5,
                  fill: "#f59e0b",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                name="In Process"
              />

              <Line
                type="monotone"
                dataKey="lost"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{
                  r: 3,
                  fill: "#ef4444",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                activeDot={{
                  r: 5,
                  fill: "#ef4444",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                name="Lost"
                strokeDasharray="5 5"
              />
            </LineChart>
          </div>
        ) : (
          <div className="no-data-message">
            <i className="ti ti-database-off "></i>
            <p>No performance data available!</p>
          </div>
        )}
      </div>

      {hasActivity && (
        <div className="scroll-hint text-info">
          <i className="ti ti-arrows-horizontal" />
          <span>Scroll horizontally to view all dates</span>
        </div>
      )}
    </div>
  );
};

export default DailyLeadPerformanceChart;
